import { createFileRoute } from '@tanstack/react-router'
import { getTranslations } from '@/i18n/translator'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, faqSchema, homeBreadcrumbItem, serviceSchema } from '@/lib/structured-data'
import { SolutionHero } from '@/features/solution-page/components/solution-hero'
import { AppShowcaseSection } from '@/features/solution-page/components/app-showcase-section'
import { FirefighterSection } from '@/features/solution-page/components/firefighter-section'
import { IncidentFlowSection } from '@/features/solution-page/components/incident-flow-section'
import { SimulatorSection } from '@/features/solution-page/components/simulator-section'
import { FaqSection } from '@/features/landing-page/components/faq-section'
import { FinalCtaSection } from '@/features/landing-page/components/final-cta-section'
import { useScrollDepth } from '@/lib/analytics/use-scroll-depth'

const PATH = '/solutions/people-security'
const FAQ_NAMESPACE = 'solutions.people.faq'
const FAQ_KEYS = ['trial', 'silent', 'training', 'offline', 'firefighters'] as const

export const Route = createFileRoute('/$locale/solutions/people-security/')({
  loader: async ({ params }) => {
    const [t, faqT] = await Promise.all([
      getTranslations({ locale: params.locale, namespace: 'solutions.people.meta' }),
      getTranslations({ locale: params.locale, namespace: FAQ_NAMESPACE }),
    ])
    const faq = FAQ_KEYS.map((key) => ({
      question: faqT(`items.${key}.question`),
      answer: faqT(`items.${key}.answer`),
    }))
    return { title: t('title'), description: t('description'), faq }
  },
  head: ({ loaderData, params }) => {
    const url = `${SITE_URL}/${params.locale}${PATH}`
    const title = loaderData?.title ?? ''
    const description = loaderData?.description ?? ''
    const jsonLd = [
      breadcrumbSchema([homeBreadcrumbItem(params.locale), { name: title, url }]),
      serviceSchema({ name: title, description, url }),
    ]
    const faqLd = faqSchema(loaderData?.faq)
    if (faqLd) jsonLd.push(faqLd)
    return pageHead({ locale: params.locale, path: PATH, title, description, jsonLd })
  },
  component: PeopleSecurity,
})

function PeopleSecurity() {
  useScrollDepth('solution')
  return (
    <>
      <SolutionHero category="people" />
      <AppShowcaseSection category="people" />
      <FirefighterSection />
      <IncidentFlowSection />
      <FaqSection
        namespace={FAQ_NAMESPACE}
        items={FAQ_KEYS}
      />
      <FinalCtaSection />
    </>
  )
}
